import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay, Navigation } from 'swiper/modules';
import { motion, useAnimation, easeOut } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';


import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import '../assets/styles/HeroSlider.css';

/* Animations */
const imageVariant = {
  hidden: { scale: 1.1, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 1, ease: easeOut } },
};

const textVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: easeOut } },
};

const statsContainerVariant = {
  hidden: { opacity: 0, y: 100, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 1, // was 1.8
      ease: easeOut,
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const statItemVariant = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easeOut } },
};

type CounterProps = {
  value: string | number;
  triggerCount: boolean;   // start the reveal count when true (after scroll)
  enableHoverRecount?: boolean;
};

const Counter: React.FC<CounterProps> = ({ value, triggerCount, enableHoverRecount = true }) => {
  const [displayCount, setDisplayCount] = useState<number | ''>('');
  const [isHovering, setIsHovering] = useState(false);
  const [hasRevealed, setHasRevealed] = useState(false);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const rawString = String(value);
  const numericMatch = rawString.match(/(\d+)/);
  const targetNumber = numericMatch ? parseInt(numericMatch[0]) : 0;
  const suffix = rawString.replace(numericMatch ? numericMatch[0] : '', '');

  // Helper: clear timer safely
  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Count once when revealed
  useEffect(() => {
    if (!triggerCount || hasRevealed) return;
    let start = 0;
    const duration = 1200;
    const step = targetNumber / (duration / 16);

    setDisplayCount(0);
    timerRef.current = setInterval(() => {
      start += step * 2;
      if (start >= targetNumber) {
        setDisplayCount(targetNumber);
        setHasRevealed(true);
        clearTimer();
      } else {
        setDisplayCount(Math.ceil(start));
      }
    }, 32);

    return clearTimer;
  }, [triggerCount, targetNumber, hasRevealed]);

  // Hover re-count
  useEffect(() => {
    if (!enableHoverRecount || !hasRevealed) return;

    if (isHovering) {
      let start = 0;
      const duration = 800;
      const step = targetNumber / (duration / 16);

      setDisplayCount(0);
      timerRef.current = setInterval(() => {
        start += step;
        if (start >= targetNumber) {
          setDisplayCount(targetNumber);
          clearTimer();
        } else {
          setDisplayCount(Math.ceil(start));
        }
      }, 16);
    } else {
      // ← KEY FIX: when mouse leaves, instantly show final value
      clearTimer();
      setDisplayCount(targetNumber);
    }

    return clearTimer;
  }, [isHovering, enableHoverRecount, hasRevealed, targetNumber]);

  if (displayCount === '' && !triggerCount) return null;

  return (
    <motion.span
      style={{ willChange: 'transform, opacity' }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      animate={isHovering ? { scale: 1.06 } : { scale: 1 }}
      transition={{ type: 'spring', stiffness: 220, damping: 15 }}
    >
      {displayCount}{suffix}
    </motion.span>
  );
};

const HeroSlider: React.FC = () => {
  const [banners, setBanners] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([
    { value: 50, label: 'Years Of Excellence' },
    { value: 14, label: 'Business Divisions' },
    { value: 300, label: 'Full-Time Workers', suffix: '+' },
    { value: 12, label: 'Strategic Locations' },
  ]);
  const [loading, setLoading] = useState(true);

  const controls = useAnimation();
  const { t_api } = useLanguage();
  const [showStats, setShowStats] = useState(false);
  // fully hide stats until user scrolls a bit
  const [countTrigger, setCountTrigger] = useState(false); // start numbers only when stats first appear
  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/homepage/content');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        const heroData = data.Hero || {};
        const fetchedBanners = [];
        for (let i = 1; i <= 9; i++) {
          const img = heroData[`banner_${i}_image`];
          const title = heroData[`banner_${i}_title`];
          const title_ar = heroData[`banner_${i}_title_ar`];
          const subtitle = heroData[`banner_${i}_subtitle`];
          const subtitle_ar = heroData[`banner_${i}_subtitle_ar`];
          if (img || title) {
            fetchedBanners.push({
              image: img || '',
              title: title || '',
              title_ar: title_ar || '',
              subtitle: subtitle || '',
              subtitle_ar: subtitle_ar || ''
            });
          }
        }
        if (fetchedBanners.length > 0) {
          setBanners(fetchedBanners);
        } else {
          // No banners configured — show a safe empty state (no hardcoded image)
          setBanners([{ title: 'Welcome to Hamdan Trading Group', subtitle: 'Part of one of Oman\'s largest conglomerates— built on growth, driven by excellence.', image: '' }]);
        }

        const statsData = data.Stats || {};
        const fetchedStats = [];
        for (let i = 1; i <= 4; i++) {
          const label = statsData[`stat_${i}_label`];
          const label_ar = statsData[`stat_${i}_label_ar`];
          const val = statsData[`stat_${i}_value`];
          if (label || val) {
            fetchedStats.push({ value: val || '0', label: label || '', label_ar: label_ar || '' });
          }
        }
        if (fetchedStats.length > 0) setStats(fetchedStats);
      } catch (err) {
        console.error('fetchData error:', err);
        setBanners([{ title: 'Welcome to Hamdan Trading Group', subtitle: 'Part of one of Oman\'s largest conglomerates— built on growth, driven by excellence.', image: '' }]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Reveal stats after a tiny scroll (no boxes or numbers on initial page entry)
  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY || 0;
      if (scrolled > 50) {
        setShowStats(true);
        setCountTrigger(true);
      } else {
        // If you *don’t* want them to re-hide when user scrolls back to top, remove this else block.
        setShowStats(false);
        setCountTrigger(false);
      }
    };
    onScroll(); // in case the user lands not at top
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Animate text overlay
  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  // In-view hook if you want extra gating (kept but not required)
  const [statsRef, statsInView] = useInView({ triggerOnce: false, threshold: 0.2 });

  return (
    <motion.section initial="hidden" animate={controls} variants={textVariant} className="relative w-full h-screen">
      {!loading && (
        <Swiper
          modules={[EffectFade, Autoplay, Navigation]}
          effect="fade"
          loop
          speed={1200}
          autoplay={{ delay: 10000, disableOnInteraction: false }}
          observer
          observeParents
          parallax
          onSwiper={setSwiperInstance}
          className="w-full h-full"
        >
          {banners.map((slide, idx) => (
            <SwiperSlide key={idx}>
              <motion.div
                key={slide.image}
                initial="hidden"
                animate="visible"
                variants={imageVariant}
                className="w-full h-full bg-cover bg-center relative flex items-center justify-center"
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                <div className="absolute inset-0 z-0 bg-black opacity-60" />

                <div className="z-10 w-full h-full flex flex-col items-center justify-center px-4 relative">
                  <motion.div className="text-center mb-4 md:mb-12 pt-16 md:pt-24 lg:pt-32" initial="hidden" animate="visible" variants={textVariant}>
                    <motion.h2 className="text-white text-3xl md:text-6xl font-bold mb-4" variants={textVariant}>
                      {t_api(slide.title, slide.title_ar)}
                    </motion.h2>
                    <motion.p className="text-white text-sm md:text-2xl max-w-3xl mx-auto px-4" variants={textVariant} transition={{ delay: 0.2 }}>
                      {t_api(slide.subtitle, slide.subtitle_ar)}
                    </motion.p>
                  </motion.div>

                  {showStats && (
                    <motion.div
                      ref={statsRef}
                      className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-8 w-full max-w-6xl px-4 absolute bottom-12 md:bottom-16 lg:bottom-20"
                      variants={statsContainerVariant}
                      initial="hidden"
                      animate="visible"
                    >
                      {stats.map((stat, index) => (
                        <motion.div
                          key={index}
                          className="bg-white/10 backdrop-blur-md rounded-lg p-2 md:p-4 border border-white/20 will-change-transform will-change-opacity"
                          variants={statItemVariant}
                          whileHover={{ scale: 1.05 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className="text-center">
                            <motion.div
                              className="text-2xl md:text-5xl font-bold text-white mb-1"
                              initial={{ scale: 0.95 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 0.35, ease: easeOut }}
                            >
                              <Counter
                                value={stat.value}
                                triggerCount={countTrigger}
                              />
                            </motion.div>
                            <div className="text-[10px] md:text-lg text-white/90 leading-tight">{t_api(stat.label, stat.label_ar)}</div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </SwiperSlide>
          ))}

          {/* Navigation Arrows */}
          <div
            onClick={() => swiperInstance?.slidePrev()}
            className="slider-prev absolute left-2 md:left-8 top-1/2 -translate-y-1/2 z-50 text-white text-4xl md:text-6xl cursor-pointer select-none transition-all duration-300 hover:scale-125 active:scale-95 opacity-60 hover:opacity-100"
          >
            ‹
          </div>
          <div
            onClick={() => swiperInstance?.slideNext()}
            className="slider-next absolute right-2 md:right-8 top-1/2 -translate-y-1/2 z-50 text-white text-4xl md:text-6xl cursor-pointer select-none transition-all duration-300 hover:scale-125 active:scale-95 opacity-60 hover:opacity-100"
          >
            ›
          </div>
        </Swiper>
      )}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-20">
          <div className="w-16 h-16 border-4 border-t-yellow-500 border-white/20 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Wave Bottom Curve */}
      <div className="absolute bottom-0 w-full overflow-hidden leading-none z-10">
        <svg className="w-full h-[60px] md:h-[80px]" viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FFFFFF" d="M0,5 C360,75 1080,75 1440,5 L1440,80 L0,80 Z"></path>
        </svg>
      </div>
    </motion.section>
  );
};

export default HeroSlider;